import { useEffect, useRef } from 'preact/hooks';

/**
 * Scroll-reveal hook. Returns a ref the caller spreads onto whichever
 * element should fade-in-up when it enters the viewport. The element
 * is expected to opt-in via `@mixin reveal` in its CSS — this hook
 * just flips the `data-reveal-in` attribute when IntersectionObserver
 * fires.
 *
 * `threshold` and `rootMargin` are tuned so the reveal triggers a hair
 * before the element is fully in view — feels more responsive than
 * waiting for it to land flush.
 *
 * One observer per call (acceptable scale at marketing-site sizes —
 * dozens of reveals max). If we wanted to be fancy, we'd share a
 * single root observer; not worth the abstraction here.
 */
export function useReveal<T extends HTMLElement = HTMLElement>(
	opts: { threshold?: number; rootMargin?: string; once?: boolean } = {},
) {
	const ref = useRef<T | null>(null);
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		// Respect reduced-motion: snap to the in state immediately.
		const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (prefersReduced) {
			el.setAttribute('data-reveal-in', '1');
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						(entry.target as HTMLElement).setAttribute('data-reveal-in', '1');
						if (opts.once !== false) observer.unobserve(entry.target);
					} else if (opts.once === false) {
						(entry.target as HTMLElement).removeAttribute('data-reveal-in');
					}
				}
			},
			{
				threshold: opts.threshold ?? 0.15,
				rootMargin: opts.rootMargin ?? '0px 0px -8% 0px',
			},
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [opts.threshold, opts.rootMargin, opts.once]);
	return ref;
}
